// src/components/management/TeamHealthCheck.js
//
// "Team checkup" for Super Admins: a card on the dashboard, and a window that
// opens by itself at most once a day when somebody needs a team decision —
// a driver with no team or no dispatcher, a dispatcher on no team, a record
// whose team does not match its company.
//
// Each row carries a suggested fix (utils/teamHealth) that can be applied as
// is, changed, or skipped. Every change goes through useTeamActions, the same
// writes the Teams screen makes, so the move rules are identical: joining a
// team of another company moves the person there.
//
// Companies that do not use teams produce no rows, so their tenants never see
// any of this.

import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTeamActions } from './useTeamActions';
import { findTeamIssues, peopleNeedingLoads, allTeamsForPicker } from '../../utils/teamHealth';
import { teamCompanyId, getUserTeamIds } from '../../utils/teams';
import { ALL_STATUSES } from '../loads/utils/constants';

const rolesOf = (u) => (Array.isArray(u?.role) ? u.role.filter(Boolean) : (u?.role ? [u.role] : []));
const REAL_STATUSES = ALL_STATUSES.filter(s => s !== 'Cancelled');
const LOADS_PER_PERSON = 10;

const today = () => new Date().toISOString().slice(0, 10);
const seenKey = (uid) => `lm_team_checkup_${uid}`;
const readSeen = (uid) => { try { return localStorage.getItem(seenKey(uid)); } catch (_) { return null; } };
const writeSeen = (uid) => { try { localStorage.setItem(seenKey(uid), today()); } catch (_) { /* private mode */ } };

export default function TeamHealthCheck({ loggedInUser }) {
  const tenantId = loggedInUser?.tenantId;
  const uid = loggedInUser?.uid || loggedInUser?.id;
  const isSuper = rolesOf(loggedInUser).includes('Super Admin');

  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [ready, setReady] = useState(0);
  const [loads, setLoads] = useState([]);
  const [open, setOpen] = useState(false);
  const [skipped, setSkipped] = useState(() => new Set());
  const [message, setMessage] = useState(null);

  const { teamBusy, assignPersonToTeam, linkDriverToDispatcher, removeLeftoverTeams } =
    useTeamActions({ loggedInUser, tenantId, users, drivers, companies, canManage: isSuper, onError: setMessage });

  useEffect(() => {
    if (!isSuper || !tenantId) return undefined;
    const mark = () => setReady(n => n + 1);
    const unsubs = [
      onSnapshot(query(collection(db, 'companies'), where('tenantId', '==', tenantId)),
        s => { setCompanies(s.docs.map(d => ({ id: d.id, ...d.data() }))); mark(); }, e => console.error('Team checkup: companies', e)),
      onSnapshot(query(collection(db, 'users'), where('tenantId', '==', tenantId)),
        s => { setUsers(s.docs.map(d => ({ id: d.id, ...d.data() }))); mark(); }, e => console.error('Team checkup: users', e)),
      onSnapshot(query(collection(db, 'drivers'), where('tenantId', '==', tenantId)),
        s => { setDrivers(s.docs.map(d => ({ id: d.id, ...d.data() }))); mark(); }, e => console.error('Team checkup: drivers', e)),
    ];
    return () => unsubs.forEach(u => u());
  }, [isSuper, tenantId]);

  const loaded = ready >= 3;
  const issues = useMemo(
    () => (loaded ? findTeamIssues({ companies, users, drivers, loads }) : []),
    [loaded, companies, users, drivers, loads]
  );
  const visible = issues.filter(i => !skipped.has(i.key));

  // Recent loads only for people the suggestion can use them for, and only
  // once the window is open: ten per person, newest first, on indexes that
  // already exist (driverId+status+tenantId+createdAt, tenantId+dispatcherId+createdAt).
  const [fetchedFor, setFetchedFor] = useState(() => new Set());
  useEffect(() => {
    if (!open || !loaded) return;
    const { driverIds, dispatcherIds } = peopleNeedingLoads(issues.filter(i => !i.suggestion || i.kind === 'user'));
    const todo = [
      ...driverIds.filter(id => !fetchedFor.has(`d:${id}`)).map(id => ({ key: `d:${id}`, q: query(collection(db, 'loads'),
        where('tenantId', '==', tenantId), where('driverId', '==', id), where('status', 'in', REAL_STATUSES),
        orderBy('createdAt', 'desc'), limit(LOADS_PER_PERSON)) })),
      ...dispatcherIds.filter(id => !fetchedFor.has(`u:${id}`)).map(id => ({ key: `u:${id}`, q: query(collection(db, 'loads'),
        where('tenantId', '==', tenantId), where('dispatcherId', '==', id),
        orderBy('createdAt', 'desc'), limit(LOADS_PER_PERSON)) })),
    ];
    if (todo.length === 0) return;
    setFetchedFor(prev => new Set([...prev, ...todo.map(t => t.key)]));
    // Promise.resolve first: a lookup that throws must not take the dashboard down.
    Promise.all(todo.map(t => Promise.resolve().then(() => getDocs(t.q)).then(s => s.docs.map(d => {
      const x = d.data();
      return { driverId: x.driverId || null, dispatcherId: x.dispatcherId || null };
    })).catch(e => { console.error('Team checkup: loads', e); return []; })))
      .then(lists => setLoads(prev => [...prev, ...lists.flat()]));
  }, [open, loaded, issues, fetchedFor, tenantId]);

  // Opens by itself once a day, and only when there is something to decide.
  useEffect(() => {
    if (!loaded || issues.length === 0 || !uid) return;
    if (readSeen(uid) === today()) return;
    writeSeen(uid);
    setOpen(true);
  }, [loaded, issues.length, uid]);

  if (!isSuper || !loaded || issues.length === 0) return null;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
        <p className="text-sm text-amber-900">
          <span className="font-semibold">⚠ Team checkup:</span>{' '}
          {issues.length} {issues.length === 1 ? 'person needs' : 'people need'} a team or dispatcher decision.
        </p>
        <button onClick={() => setOpen(true)}
          className="px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium">
          Review
        </button>
      </div>

      {open && (
        <CheckupWindow
          issues={visible}
          total={issues.length}
          companies={companies}
          users={users}
          drivers={drivers}
          busy={teamBusy}
          message={message}
          onClose={() => setOpen(false)}
          onSkip={(key) => setSkipped(prev => new Set([...prev, key]))}
          onApply={async (issue, choice) => {
            setMessage(null);
            if (issue.kind === 'leftover') {
              await removeLeftoverTeams(issue.id);
              return;
            }
            const company = companies.find(c => c.id === teamCompanyId(choice.teamId));
            if (!company) return;
            if (issue.kind === 'driver') {
              const driver = drivers.find(d => d.id === issue.id);
              if (driver?.teamId !== choice.teamId || driver?.parentCompanyId !== company.id) {
                await assignPersonToTeam(company, { kind: 'driver', id: issue.id, teamId: choice.teamId });
              }
              if (choice.dispatcherId) await linkDriverToDispatcher(issue.id, choice.dispatcherId);
            } else {
              await assignPersonToTeam(company, { kind: 'user', id: issue.id, teamId: choice.teamId });
            }
          }}
        />
      )}
    </>
  );
}

// --------------------------------------------------------------------------
function CheckupWindow({ issues, total, companies, users, drivers, busy, message, onClose, onSkip, onApply }) {
  const teams = useMemo(() => allTeamsForPicker(companies), [companies]);
  const teamLabel = (id) => teams.find(t => t.id === id)?.label || '—';
  const nameOf = (id) => { const u = users.find(x => x.id === id); return u?.name || u?.email || '—'; };
  const [choices, setChoices] = useState({});   // key → { teamId, dispatcherId }
  const [editing, setEditing] = useState(null);
  const [working, setWorking] = useState(false);

  const choiceFor = (i) => choices[i.key] || (i.suggestion && !i.suggestion.cleanup
    ? { teamId: i.suggestion.teamId, dispatcherId: i.suggestion.dispatcherId || '' }
    : null);

  const dispatchersOf = (teamId) => users.filter(u => u.active !== false &&
    rolesOf(u).includes('Dispatcher') && !rolesOf(u).includes('Main Admin') &&
    getUserTeamIds(u).includes(teamId));

  // Whether applying moves this person into another company — said in the row.
  const movesCompany = (i, teamId) => {
    const target = teamCompanyId(teamId);
    if (!target) return null;
    if (i.kind === 'driver') {
      const d = drivers.find(x => x.id === i.id);
      return d?.parentCompanyId !== target ? companies.find(c => c.id === target)?.name : null;
    }
    const u = users.find(x => x.id === i.id);
    return (u?.assignedParentCompanyIds || []).includes(target) ? null : companies.find(c => c.id === target)?.name;
  };

  const applyOne = async (i) => {
    const c = choiceFor(i);
    if (i.kind !== 'leftover' && !c?.teamId) return;
    setWorking(true);
    try { await onApply(i, c || {}); } finally { setWorking(false); setEditing(null); }
  };

  const ready = issues.filter(i => i.kind === 'leftover' || choiceFor(i)?.teamId);
  const applyAll = async () => {
    const moves = ready.filter(i => i.kind !== 'leftover' && movesCompany(i, choiceFor(i)?.teamId)).length;
    if (!window.confirm(
      `Apply ${ready.length} suggestion${ready.length === 1 ? '' : 's'}?` +
      (moves ? `\n\n${moves} of them move${moves === 1 ? 's' : ''} somebody to another company.` : '')
    )) return;
    setWorking(true);
    try { for (const i of ready) await onApply(i, choiceFor(i) || {}); } finally { setWorking(false); }
  };

  const disabled = busy || working;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Team checkup</h2>
            <p className="text-sm text-gray-500">
              {issues.length === 0
                ? 'All done for now.'
                : `${issues.length} ${issues.length === 1 ? 'person needs' : 'people need'} a decision. Each has a suggestion — apply it, change it, or skip.`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" aria-label="Close">×</button>
        </div>

        {message?.type === 'error' && (
          <p className="mx-6 mt-3 px-3 py-2 rounded-md text-sm bg-red-50 text-red-800 border border-red-200">{message.text}</p>
        )}

        <div className="overflow-y-auto px-6 py-3 divide-y divide-gray-100">
          {issues.map(i => {
            const c = choiceFor(i);
            const moving = c?.teamId ? movesCompany(i, c.teamId) : null;
            const isEditing = editing === i.key;
            return (
              <div key={i.key} className="py-3 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[260px]">
                  <p className="text-sm font-medium text-gray-900">
                    {i.name}{' '}
                    <span className="text-[11px] font-normal text-gray-500 uppercase tracking-wide">
                      {i.kind === 'driver' ? 'Driver' : i.kind === 'user' ? 'Dispatcher' : 'User'}
                    </span>
                  </p>
                  <p className="text-xs text-amber-700">⚠ {i.problem}</p>

                  {i.kind === 'leftover' ? (
                    <p className="text-xs text-gray-600 mt-1">{i.suggestion.reason}</p>
                  ) : isEditing ? (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <select value={c?.teamId || ''} className="border border-gray-300 rounded px-2 py-1 text-xs"
                        onChange={(e) => {
                          const only = dispatchersOf(e.target.value);
                          setChoices(p => ({ ...p, [i.key]: { teamId: e.target.value, dispatcherId: only.length === 1 ? only[0].id : '' } }));
                        }}>
                        <option value="">Choose a team…</option>
                        {teams.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                      {i.kind === 'driver' && c?.teamId && (
                        <select value={c?.dispatcherId || ''} className="border border-gray-300 rounded px-2 py-1 text-xs"
                          onChange={(e) => setChoices(p => ({ ...p, [i.key]: { ...c, dispatcherId: e.target.value } }))}>
                          <option value="">Dispatcher: choose later</option>
                          {dispatchersOf(c.teamId).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                        </select>
                      )}
                    </div>
                  ) : c?.teamId ? (
                    <p className="text-xs text-gray-700 mt-1">
                      <span className="font-medium text-blue-800">Suggested:</span> {teamLabel(c.teamId)}
                      {i.kind === 'driver' && c.dispatcherId ? `, dispatcher ${nameOf(c.dispatcherId)}` : ''}
                      {!choices[i.key] && i.suggestion?.reason && <span className="text-gray-500"> — {i.suggestion.reason}</span>}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">No suggestion — nothing to go on yet. Choose a team.</p>
                  )}

                  {moving && i.kind !== 'leftover' && (
                    <p className="text-[11px] text-amber-800 mt-0.5">Moves them to {moving}.</p>
                  )}
                </div>

                <div className="flex gap-2 shrink-0">
                  {i.kind !== 'leftover' && (
                    <button onClick={() => setEditing(isEditing ? null : i.key)} disabled={disabled}
                      className="px-2.5 py-1 rounded border text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      {isEditing ? 'Done' : c?.teamId ? 'Change' : 'Choose'}
                    </button>
                  )}
                  <button onClick={() => applyOne(i)} disabled={disabled || (i.kind !== 'leftover' && !c?.teamId)}
                    className="px-2.5 py-1 rounded bg-blue-600 text-white text-xs font-medium disabled:opacity-40">
                    {i.kind === 'leftover' ? 'Clean up' : 'Apply'}
                  </button>
                  <button onClick={() => onSkip(i.key)} disabled={disabled}
                    className="px-2.5 py-1 rounded text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50">
                    Skip
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {total > issues.length ? `${total - issues.length} skipped. ` : ''}
            Opens by itself once a day. The dashboard card stays until everything is sorted.
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded-md border text-sm text-gray-700 hover:bg-gray-50">
              Skip for today
            </button>
            {ready.length > 0 && (
              <button onClick={applyAll} disabled={disabled}
                className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50">
                {working ? 'Applying…' : `Apply all suggestions (${ready.length})`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
